import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/ui/password-input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { UserPlus, Trash2, Pause, Play, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function SuperAdminAdminsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [busy, setBusy] = useState(false);

  // Verifier accounts — narrow-role staff who can look up + redeem
  // coupons across all vendors. Managed on the same page as admins.
  const [verifiers, setVerifiers] = useState([]);
  const [verifiersLoading, setVerifiersLoading] = useState(true);
  const [vForm, setVForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [vBusy, setVBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.superadminListAdmins();
      setItems(r.data || []);
    } finally {
      setLoading(false);
    }
  };
  const loadVerifiers = async () => {
    setVerifiersLoading(true);
    try {
      const r = await api.superadminListVerifiers();
      setVerifiers(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load verifiers");
    } finally {
      setVerifiersLoading(false);
    }
  };
  useEffect(() => {
    load();
    loadVerifiers();
  }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.password) return toast.error("Fill all fields");
    setBusy(true);
    try {
      await api.superadminCreateAdmin(form);
      toast.success("Admin created");
      setForm({ name: "", email: "", phone: "", password: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const suspend = async (id) => {
    if (!window.confirm("Suspend this admin?")) return;
    await api.superadminSuspendAdmin(id);
    toast.success("Suspended");
    load();
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this admin?")) return;
    await api.superadminDeleteAdmin(id);
    toast.success("Deleted");
    load();
  };

  // ── Verifier CRUD ────────────────────────────────────────────────
  const createVerifier = async (e) => {
    e.preventDefault();
    if (!vForm.name || !vForm.email || !vForm.password) {
      return toast.error("Name, email, and password are required.");
    }
    setVBusy(true);
    try {
      await api.superadminCreateVerifier(vForm);
      toast.success("Verifier created");
      setVForm({ name: "", email: "", phone: "", password: "" });
      loadVerifiers();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setVBusy(false);
    }
  };
  const suspendVerifier = async (id) => {
    if (!window.confirm("Suspend this verifier?")) return;
    try {
      await api.superadminSuspendVerifier(id);
      toast.success("Verifier suspended");
      loadVerifiers();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };
  const reactivateVerifier = async (id) => {
    try {
      await api.superadminReactivateVerifier(id);
      toast.success("Verifier reactivated");
      loadVerifiers();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };
  const deleteVerifier = async (id) => {
    if (!window.confirm("Delete this verifier?")) return;
    try {
      await api.superadminDeleteVerifier(id);
      toast.success("Verifier deleted");
      loadVerifiers();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-3xl font-semibold">Manage admins</h1>
        <p className="text-muted-foreground text-sm">Create and revoke Admin accounts.</p>
      </div>
      <Card className="p-5">
        <h2 className="font-heading text-base font-semibold mb-3 flex items-center gap-2"><UserPlus className="h-4 w-4" /> Create admin</h2>
        <form onSubmit={create} className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="space-y-1.5"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="sa-create-admin-name" /></div>
          <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="sa-create-admin-email" /></div>
          <div className="space-y-1.5"><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="sa-create-admin-phone" /></div>
          <div className="space-y-1.5"><Label>Password</Label><PasswordInput value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="sa-create-admin-password" /></div>
          <div className="md:col-span-4 flex justify-end">
            <Button disabled={busy} data-testid="sa-create-admin-submit">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><UserPlus className="h-4 w-4 mr-1" /> Create admin</>}
            </Button>
          </div>
        </form>
      </Card>
      <Card className="p-5">
        <h2 className="font-heading text-base font-semibold mb-3">Admin accounts</h2>
        {loading ? <Skeleton className="h-40 w-full" /> : items.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="sa-admins-empty">No admin accounts yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="sa-admins-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground border-b border-border">
                  <th className="py-2 px-3">Name</th>
                  <th className="py-2 px-3">Email</th>
                  <th className="py-2 px-3">Phone</th>
                  <th className="py-2 px-3">Status</th>
                  <th className="py-2 px-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((a) => (
                  <tr key={a.id} className="border-b last:border-0 border-border" data-testid="sa-admin-row">
                    <td className="py-2 px-3 font-medium">{a.name}</td>
                    <td className="py-2 px-3 text-muted-foreground">{a.email}</td>
                    <td className="py-2 px-3 text-muted-foreground">{a.phone}</td>
                    <td className="py-2 px-3">
                      {a.is_active ? <Badge variant="outline" className="text-emerald-400 border-emerald-400/40">Active</Badge> : <Badge variant="outline" className="text-red-400 border-red-400/40">Suspended</Badge>}
                    </td>
                    <td className="py-2 px-3 text-right space-x-2">
                      {a.is_active && <Button variant="outline" size="sm" onClick={() => suspend(a.id)} data-testid="sa-admin-suspend"><Pause className="h-4 w-4" /></Button>}
                      <Button variant="destructive" size="sm" onClick={() => remove(a.id)} data-testid="sa-admin-delete"><Trash2 className="h-4 w-4" /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* ── Verifier accounts ─────────────────────────────────────────
          Narrow-role staff (part-time / event) who can search + redeem
          coupons across ALL vendors from the /verify page. They cannot
          see pricing, vendors, wallet, or any other admin surface. */}
      <div className="pt-4">
        <h2 className="font-heading text-2xl font-semibold flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-primary" aria-hidden />
          Coupon Verifiers
        </h2>
        <p className="text-muted-foreground text-sm mt-1">
          Provision on-ground staff who can look up and redeem winning coupons at any store.
          They sign in at <span className="font-mono">/verify</span> and see nothing else.
        </p>
      </div>

      <Card className="p-5">
        <h3 className="font-heading text-base font-semibold mb-3 flex items-center gap-2">
          <UserPlus className="h-4 w-4" /> Create verifier
        </h3>
        <form onSubmit={createVerifier} className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="space-y-1.5">
            <Label>Name</Label>
            <Input value={vForm.name} onChange={(e) => setVForm({ ...vForm, name: e.target.value })} data-testid="sa-create-verifier-name" />
          </div>
          <div className="space-y-1.5">
            <Label>Email</Label>
            <Input type="email" value={vForm.email} onChange={(e) => setVForm({ ...vForm, email: e.target.value })} data-testid="sa-create-verifier-email" />
          </div>
          <div className="space-y-1.5">
            <Label>Phone (optional)</Label>
            <Input value={vForm.phone} onChange={(e) => setVForm({ ...vForm, phone: e.target.value })} data-testid="sa-create-verifier-phone" />
          </div>
          <div className="space-y-1.5">
            <Label>Password</Label>
            <PasswordInput value={vForm.password} onChange={(e) => setVForm({ ...vForm, password: e.target.value })} data-testid="sa-create-verifier-password" />
          </div>
          <div className="md:col-span-4 flex justify-end">
            <Button disabled={vBusy} data-testid="sa-create-verifier-submit">
              {vBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><UserPlus className="h-4 w-4 mr-1" /> Create verifier</>}
            </Button>
          </div>
        </form>
      </Card>

      <Card className="p-5">
        <h3 className="font-heading text-base font-semibold mb-3">Verifier accounts</h3>
        {verifiersLoading ? (
          <Skeleton className="h-40 w-full" />
        ) : verifiers.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="sa-verifiers-empty">
            No verifier accounts yet.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="sa-verifiers-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground border-b border-border">
                  <th className="py-2 px-3">Name</th>
                  <th className="py-2 px-3">Email</th>
                  <th className="py-2 px-3">Phone</th>
                  <th className="py-2 px-3">Status</th>
                  <th className="py-2 px-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {verifiers.map((v) => (
                  <tr key={v.id} className="border-b last:border-0 border-border" data-testid="sa-verifier-row">
                    <td className="py-2 px-3 font-medium">{v.name}</td>
                    <td className="py-2 px-3 text-muted-foreground">{v.email}</td>
                    <td className="py-2 px-3 text-muted-foreground">{v.phone || "—"}</td>
                    <td className="py-2 px-3">
                      {v.is_active ? (
                        <Badge variant="outline" className="text-emerald-400 border-emerald-400/40">Active</Badge>
                      ) : (
                        <Badge variant="outline" className="text-red-400 border-red-400/40">Suspended</Badge>
                      )}
                    </td>
                    <td className="py-2 px-3 text-right space-x-2">
                      {v.is_active ? (
                        <Button variant="outline" size="sm" onClick={() => suspendVerifier(v.id)} data-testid="sa-verifier-suspend">
                          <Pause className="h-4 w-4" />
                        </Button>
                      ) : (
                        <Button variant="outline" size="sm" onClick={() => reactivateVerifier(v.id)} data-testid="sa-verifier-reactivate">
                          <Play className="h-4 w-4" />
                        </Button>
                      )}
                      <Button variant="destructive" size="sm" onClick={() => deleteVerifier(v.id)} data-testid="sa-verifier-delete">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
